import { useFormContext } from 'react-hook-form';
import { ColumnDef } from '@tanstack/react-table';
import { linkOptions, useSearch } from '@tanstack/react-router';
import { cn } from '@anytag/shared-utils';
import { useMarketplacePermissions, useUserRoles } from '@anytag/auth';
import { InfluencerAvatar, MarketplacePostStatus } from '@anytag/shared-molecules';
import {
  useQueryHelper,
  useSelectItems,
  useTableInputCursor,
  useGenerateInfluencerProfileLink
} from '@anytag/shared-hooks';
import {
  Icon,
  Anchor,
  Tooltip,
  CheckBox,
  TableCell,
  SocialIcon,
  MediaPreview,
  SocialIconProps
} from '@anytag/shared-atoms';
import {
  MarketplacePost,
  MarketplaceDetail,
  MarketplaceCampaignPostStatusForAdvertiser,
  MarketplaceCampaignRevenueType
} from '@anytag/graphql';
import { isVideoFileUrl } from '@anytag/shared-utils';
import { MarketplacePostsSearchSchemaTypes } from '../../MarketplacePostsFilter';
import { UpdateCostsFormValues, usePostPricesAvailability } from '../../../hooks';
import { findRevenueByType } from '../../../MarketplaceJoinedInfluencers/utils';

interface Props {
  hasTrackingReferral?: boolean;
  marketplace: MarketplaceDetail;
  posts: readonly MarketplacePost[];
}

export const useMarketplacePostsListTable = ({
  posts,
  hasTrackingReferral,
  marketplace: { isGiftingCampaign, socialMedia, currency, id: mkpId }
}: Props) => {
  const searchParams = useSearch({
    from: '/_private-routes/marketplace/$id/posts'
  }) as MarketplacePostsSearchSchemaTypes;

  const { t, navigate } = useQueryHelper();
  const { control } = useFormContext<UpdateCostsFormValues>();
  const { generateProfileLink } = useGenerateInfluencerProfileLink();
  const { isAdvertiser, isAdminStaffAgency, isAdminStaff } = useUserRoles();
  const { handleInputFocus, handleInputBlur } = useTableInputCursor<UpdateCostsFormValues>();
  const { hideApproveRejectInappropriatePostBtn } = useMarketplacePermissions();
  const allPostPrices = usePostPricesAvailability(socialMedia);

  const checkHasPostStatusControl = (post: Pick<MarketplacePost, 'id' | 'status'>) =>
    !hideApproveRejectInappropriatePostBtn &&
    post.id &&
    post.status &&
    [
      MarketplaceCampaignPostStatusForAdvertiser.EFFECTIVE,
      MarketplaceCampaignPostStatusForAdvertiser.WARNING,
      ...(isGiftingCampaign ? [MarketplaceCampaignPostStatusForAdvertiser.JOINED] : [])
    ].includes(post.status);

  const postsActions = useSelectItems<MarketplacePost, keyof MarketplacePost>({
    key: 'id',
    listItems: posts.reduce<MarketplacePost[]>(
      (acc, curr) => (checkHasPostStatusControl(curr) ? [...acc, curr] : acc),
      []
    )
  });

  const { allItemIds, selectedItems, selectItem, toggleSelectAllItems } = postsActions;
  const columns: ColumnDef<MarketplacePost>[] = [
    {
      size: 40,
      accessorKey: 'action',
      meta: { sticky: true, hidden: hideApproveRejectInappropriatePostBtn },
      header: () => <CheckBox onClick={toggleSelectAllItems} disabled={!allItemIds.length} help={t('Select all')} />,
      cell: ({ row }) => {
        const { id, status, influencer } = row.original;
        const isSelectedPost = selectedItems.findIndex((p) => p.id === id && p.influencer.id === influencer.id) !== -1;

        return checkHasPostStatusControl({ id, status }) ? (
          <CheckBox
            checked={isSelectedPost}
            onClick={id ? () => selectItem(id) : undefined}
            help={t(isSelectedPost ? 'Unselect' : 'Select')}
          />
        ) : (
          <div className="w-4" />
        );
      }
    },
    {
      accessorKey: 'influencer',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Influencer'),
      cell: ({ row }) => {
        const { influencer } = row.original;

        return (
          <TableCell.Link
            label={influencer.name}
            className="flex w-50 items-center"
            {...generateProfileLink(influencer.id)}
          >
            <InfluencerAvatar size="40px" src={influencer.avatar} asChild />
            <TableCell.Text className="ml-2 min-w-0" value={influencer.name} />
          </TableCell.Link>
        );
      }
    },
    {
      accessorKey: 'content',
      header: t<string>('HeaderColumn.Content'),
      cell: ({ row }) => {
        const { id, draftId, status, content, thumbNail, needReview } = row.original;
        const isDraftPost = !!draftId && !id && (isAdvertiser || isAdminStaffAgency);
        const isReviewingDraftPost = isDraftPost && status === MarketplaceCampaignPostStatusForAdvertiser.DRAFT;
        const postDetailsUrl =
          !!id || !!draftId
            ? linkOptions({
                to: '/marketplace/$id/posts/$postId',
                params: { id: mkpId.toString(), postId: isDraftPost ? draftId.toString() : id?.toString() || '' },
                search: {
                  ...searchParams,
                  filter: { ...searchParams.filter, draftPost: draftId ? Boolean(true).toString() : null }
                }
              })
            : undefined;

        const goToPostDetails = () => {
          if (postDetailsUrl) {
            navigate(postDetailsUrl);
          }
        };
        const isVideoPreview = isVideoFileUrl(thumbNail);
        const previewSrc = isVideoPreview ? null : thumbNail;

        return (
          <div
            onClick={goToPostDetails}
            className={cn('flex items-center gap-2', { 'cursor-pointer': postDetailsUrl })}
          >
            <MediaPreview
              src={previewSrc}
              className="h-10 w-15"
              postReportCaption={isVideoPreview ? <div /> : undefined}
            />
            <Anchor
              lines={2}
              className="w-max max-w-75"
              variant={isReviewingDraftPost ? 'blue' : 'default'}
              label={isReviewingDraftPost ? t('Review draft post') : content || '-'}
            />
            {needReview ? (
              <Tooltip place="top" help={t('Tooltip.Post is modified, please review')}>
                <div className="ml-1">
                  <Icon icon="warning" size={16} variant="green" />
                </div>
              </Tooltip>
            ) : undefined}
          </div>
        );
      }
    },
    {
      accessorKey: 'referralCode',
      meta: { hidden: !hasTrackingReferral },
      header: t<string>('HeaderColumn.Referral Code'),
      cell: ({ row }) => <TableCell.Text value={row.original.couponCode || ''} className="min-w-20" />
    },
    {
      accessorKey: 'socialMedia',
      header: t<string>('HeaderColumn.Social'),
      meta: { align: 'center' },
      cell: ({ row }) => (
        <div className="mx-auto block w-max">
          <SocialIcon type={row.original.socialMedia as SocialIconProps['type']} />
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: t<string>('HeaderColumn.Post Status'),
      cell: ({ row }) =>
        row.original.status ? (
          <MarketplacePostStatus status={row.original.status} warningReason={row.original.warningReason || undefined} />
        ) : (
          ''
        )
    },
    {
      accessorKey: 'revenuePerPost',
      header: t<string>('HeaderColumn.Revenue per Posting'),
      meta: { hidden: !isAdminStaff || !allPostPrices.costPerPost },
      cell: ({ row }) => {
        const { influencer, revenueItems, costEditable } = row.original;
        const revenuePerPost = findRevenueByType(revenueItems, MarketplaceCampaignRevenueType.REVENUE_PER_POST);

        return (
          <TableCell.ControlledRevenueInput
            rightSection={currency}
            control={control}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            disabled={!costEditable}
            updatedDate={revenuePerPost?.updatedDate}
            name={`${influencer.id}_revenuePerPost`}
          />
        );
      }
    },
    {
      accessorKey: 'revenuePerLike',
      header: t<string>('HeaderColumn.Revenue per Like'),
      meta: { hidden: !isAdminStaff || !allPostPrices.costPerLike },
      cell: ({ row }) => {
        const { influencer, revenueItems, costEditable } = row.original;
        const revenuePerLike = findRevenueByType(revenueItems, MarketplaceCampaignRevenueType.REVENUE_PER_LIKE);

        return (
          <TableCell.ControlledRevenueInput
            rightSection={currency}
            control={control}
            disabled={!costEditable}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            updatedDate={revenuePerLike?.updatedDate}
            name={`${influencer.id}_revenuePerLike`}
          />
        );
      }
    },
    {
      accessorKey: 'revenuePerView',
      header: t<string>('HeaderColumn.Revenue per View'),
      meta: { hidden: !isAdminStaff || !allPostPrices.costPerView },
      cell: ({ row }) => {
        const { influencer, revenueItems, costEditable } = row.original;
        const revenuePerView = findRevenueByType(revenueItems, MarketplaceCampaignRevenueType.REVENUE_PER_VIEW);

        return (
          <TableCell.ControlledRevenueInput
            rightSection={currency}
            control={control}
            disabled={!costEditable}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            updatedDate={revenuePerView?.updatedDate}
            name={`${influencer.id}_revenuePerView`}
          />
        );
      }
    },
    {
      accessorKey: 'revenuePerShare',
      header: t<string>('HeaderColumn.Revenue per Share'),
      meta: { hidden: !isAdminStaff || !allPostPrices.costPerShare },
      cell: ({ row }) => {
        const { influencer, revenueItems, costEditable } = row.original;
        const revenuePerShare = findRevenueByType(revenueItems, MarketplaceCampaignRevenueType.REVENUE_PER_SHARE);

        return (
          <TableCell.ControlledRevenueInput
            rightSection={currency}
            control={control}
            disabled={!costEditable}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            updatedDate={revenuePerShare?.updatedDate}
            name={`${influencer.id}_revenuePerShare`}
          />
        );
      }
    },
    {
      accessorKey: 'revenuePerClick',
      header: t<string>('HeaderColumn.Revenue per Click'),
      meta: { hidden: !isAdminStaff || !allPostPrices.costPerClick },
      cell: ({ row }) => {
        const { influencer, revenueItems, costEditable } = row.original;
        const revenuePerClick = findRevenueByType(revenueItems, MarketplaceCampaignRevenueType.REVENUE_PER_CLICK);

        return (
          <TableCell.ControlledRevenueInput
            rightSection={currency}
            control={control}
            disabled={!costEditable}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            updatedDate={revenuePerClick?.updatedDate}
            name={`${influencer.id}_revenuePerClick`}
          />
        );
      }
    },
    {
      accessorKey: 'revenuePerComment',
      header: t<string>('HeaderColumn.Revenue per Comment'),
      meta: { hidden: !isAdminStaff || !allPostPrices.costPerComment },
      cell: ({ row }) => {
        const { influencer, revenueItems, costEditable } = row.original;
        const revenuePerComment = findRevenueByType(revenueItems, MarketplaceCampaignRevenueType.REVENUE_PER_COMMENT);

        return (
          <TableCell.ControlledRevenueInput
            rightSection={currency}
            control={control}
            disabled={!costEditable}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            updatedDate={revenuePerComment?.updatedDate}
            name={`${influencer.id}_revenuePerComment`}
          />
        );
      }
    },
    {
      accessorKey: 'revenuePerAction',
      header: t<string>('HeaderColumn.Revenue per Conversion'),
      meta: { hidden: !isAdminStaff || !allPostPrices.costPerAction },
      cell: ({ row }) => {
        const { influencer, revenueItems, costEditable } = row.original;
        const revenuePerAction = findRevenueByType(revenueItems, MarketplaceCampaignRevenueType.REVENUE_PER_ACTION);

        return (
          <TableCell.ControlledRevenueInput
            rightSection={currency}
            control={control}
            disabled={!costEditable}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            updatedDate={revenuePerAction?.updatedDate}
            name={`${influencer.id}_revenuePerAction`}
          />
        );
      }
    },
    {
      accessorKey: 'postedDate',
      header: t<string>('HeaderColumn.Post date'),
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.postedDate || ''} />
    }
  ];

  return { columns, postsActions };
};
